<template>
	<BlockVisibilityObserver title="PROVIDERS">
		<section class="kova-v2-providers">
			<h2 class="kova-v2-providers__title">{{ title }}</h2>

			<LandingKovaCarousel :count="items.length" item-name="provider" :label="title" theme="providers">
				<article
					v-for="(item, index) in items"
					:key="item.name"
					:aria-label="`Provider ${index + 1} of ${items.length}`"
					class="kova-carousel__slide kova-v2-providers__slide"
				>
					<div class="kova-v2-providers__card">
						<span class="kova-v2-providers__portrait" :class="`kova-v2-providers__portrait_${item.provider}`">
							<img
								:alt="item.name"
								height="132"
								decoding="async"
								loading="lazy"
								:src="PORTRAITS[item.provider]"
								width="132"
							/>
						</span>

						<div class="kova-v2-providers__copy">
							<h3 class="kova-v2-providers__name">{{ item.name }}</h3>
							<p class="kova-v2-providers__bio">{{ item.bio }}</p>
						</div>
					</div>
				</article>
			</LandingKovaCarousel>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaCarousel from '@/modules/Landing/components/LandingKovaCarousel.vue'
import { LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import providerCraig from '@/assets/kova/landing/v2/provider-craig.webp'
import providerKimberli from '@/assets/kova/landing/v2/provider-kimberli.webp'
import providerLaurie from '@/assets/kova/landing/v2/provider-laurie.webp'
import providerTheresa from '@/assets/kova/landing/v2/provider-theresa.webp'

defineOptions({ name: 'LandingKovaV2Providers' })

const PORTRAITS = {
	laurie: providerLaurie,
	kimberli: providerKimberli,
	theresa: providerTheresa,
	craig: providerCraig,
}

defineProps<LandingComponentProps.KovaV2Providers>()
</script>

<style lang="scss" scoped>
// Mobile (6461:63184): 20 top, 32 bottom, 16px gutters. The portrait hangs 92px above
// its card, so the 116px heading gap is 24 + that overhang, kept inside each slide (a
// scroll container would clip anything above it).
.kova-v2-providers {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-20) var(--size-16) var(--size-32);

	font-family: var(--kova-lp-font);
}

.kova-v2-providers__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36); // 32px × 1.12 = 35.84px — exact size not in design system
	letter-spacing: -0.64px;
	text-align: center;

	color: var(--kova-lp-ink);
}

.kova-v2-providers__slide {
	box-sizing: border-box;
	display: flex;
	padding-top: var(--size-96); // Closest to the 92px portrait overhang in Figma 6461:63191.
}

// 6461:63187 — white card, 54 top (under the portrait), 20 sides/bottom, 32 radius.
.kova-v2-providers__card {
	position: relative;
	box-sizing: border-box;
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	align-items: center;
	padding: var(--size-54) var(--size-20) var(--size-20);

	border-radius: var(--size-32);
	background: var(--Fill-White);
}

// 6461:63191 — 132px circle, 1px hairline, centred, 92px above the card.
.kova-v2-providers__portrait {
	position: absolute;
	left: 50%;
	top: calc(-1 * var(--size-96));
	overflow: hidden;
	box-sizing: border-box;

	width: var(--size-128); // Closest to 132px in Figma 6461:63191.
	height: var(--size-128);

	background: var(--kova-v2-portrait);
	border: 1px solid var(--kova-v2-portrait-ring);
	border-radius: 50%;
	transform: translateX(-50%);

	img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: top;
	}
}

.kova-v2-providers__portrait_laurie {
	background: var(--Fill-White);

	// Figma 6557:32732 / 6461:63192: 161.33 × 242 at (-14.67, -39.11) in a 132px circle.
	img {
		position: absolute;
		left: -11.11%;
		top: -29.63%;
		width: 122.22%;
		max-width: none;
		height: 183.33%;
	}
}

.kova-v2-providers__portrait_craig img {
	// Figma 6557:32751: 162 × 132 at (-8, 7), centred on the face rather than the photo.
	position: absolute;
	left: -6.06%;
	top: 5.3%;
	width: 122.73%;
	max-width: none;
	height: 100%;
}

.kova-v2-providers__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);

	text-align: center;
	color: var(--Text-Primary-100);
}

// 6461:63189 — Poppins Medium 19 / 1.36.
.kova-v2-providers__name {
	margin: 0;

	font-size: var(--size-18); // Closest to 19px in Figma 6461:63189.
	font-weight: 500;
	line-height: var(--size-26); // 19px × 1.36 = 25.84px — exact size not in design system
	letter-spacing: -0.19px;
}

.kova-v2-providers__bio {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);
}

// Desktop (6557:32477): 60px block padding, two 592 columns 16 apart — Laurie over
// Theresa, Kimberli over Craig. Three shared rows reproduce the drawn stagger with the
// DOM (and reading) order Laurie, Kimberli, Theresa, Craig: Laurie spans rows 1-2,
// Craig rows 2-3, every card keeps its own height.
@media (min-width: 1024px) {
	.kova-v2-providers {
		box-sizing: border-box;
		gap: var(--size-40);
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-60) var(--size-40);
	}

	.kova-v2-providers__title {
		font-size: var(--size-56);
		line-height: var(--size-60); // 56px × 1.1 = 61.60px — exact size not in design system
		letter-spacing: -1.12px;
	}

	.kova-v2-providers__slide {
		padding: 0;
	}

	// 6557:32730 — portrait left, copy right; 32/40/32 padding.
	.kova-v2-providers__card {
		flex-direction: row;
		align-items: flex-start;
		gap: var(--size-16);
		padding: var(--size-32) var(--size-40) var(--size-32) var(--size-32);
	}

	.kova-v2-providers__portrait {
		position: relative;
		left: auto;
		top: auto;
		flex-shrink: 0;

		transform: none;
	}

	.kova-v2-providers__copy {
		flex: 1 1 0;
		min-width: 0;

		text-align: left;
	}

	.kova-v2-providers__name {
		font-size: var(--size-20);
		line-height: var(--size-24);
		letter-spacing: -0.4px;

		color: var(--kova-lp-ink);
	}
}

@media (min-width: 1440px) {
	.kova-v2-providers {
		padding-inline: var(--size-120);
	}
}
</style>
